import { debounce } from '@/lib/lodash'

const DEFAUL_FONT_SIZES = [
  16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 46, 52, 62, 72, 82,
]

const BLOCK_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'td',
  'th',
  'caption',
])

const cleanupNonSizableTags = (
  editor: CKEDITOR.editor,
  nonSizableTags?: string[]
): boolean => {
  if (!nonSizableTags?.length) {
    return false
  }

  const NON_SIZABLE_SELECTOR = nonSizableTags.reduce<string>((acc, tag) => {
    acc += acc ? ',' : ''

    return acc + `${tag}[data-font-size]`
  }, '')

  const body = editor.document.getBody()
  const found = body.find(NON_SIZABLE_SELECTOR)

  if (found.count() === 0) {
    return false
  }

  for (let i = 0; i < found.count(); i++) {
    const el = found.getItem(i) as CKEDITOR.dom.element

    el.removeAttribute('data-font-size')
  }

  return true
}

const selectionHasNonSizableTag = (
  editor: CKEDITOR.editor,
  nonSizableTags?: string[]
): boolean => {
  if (!nonSizableTags?.length) {
    return false
  }

  let blocks = getSelectedRootBlocks(editor)

  if (blocks.length === 0) {
    const startEl = editor.getSelection()?.getStartElement() ?? null
    const block = getRootBlock(startEl)

    blocks = block ? [block] : []
  }

  if (blocks.length === 0) {
    return false
  }

  return blocks.some((block) => nonSizableTags.includes(block.getName()))
}

const getRootBlock = (
  el: CKEDITOR.dom.node | null
): CKEDITOR.dom.element | null => {
  let block: CKEDITOR.dom.element | null = null

  let cursor: CKEDITOR.dom.element | null =
    el?.type === CKEDITOR.NODE_TEXT
      ? (el.getParent() as CKEDITOR.dom.element)
      : (el as CKEDITOR.dom.element | null)

  while (cursor?.type === CKEDITOR.NODE_ELEMENT) {
    const name = cursor.getName()

    if (name === 'ol' || name === 'ul') {
      return cursor
    }

    if (BLOCK_TAGS.has(name) && block === null) {
      block = cursor
    }

    cursor = cursor.getParent()
  }

  return block
}

const getSelectedRootBlocks = (
  editor: CKEDITOR.editor
): CKEDITOR.dom.element[] => {
  const selection = editor.getSelection()
  if (!selection) return []

  const ranges = selection.getRanges()
  const seen = new Set<string>()
  const blocks: CKEDITOR.dom.element[] = []

  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i]
    const walker = new CKEDITOR.dom.walker(range)

    walker.evaluator = (node: CKEDITOR.dom.node) => {
      if (node.type !== CKEDITOR.NODE_ELEMENT) return false

      const el = node as CKEDITOR.dom.element
      const name = el.getName()

      return BLOCK_TAGS.has(name) || name === 'ol' || name === 'ul'
    }

    const startBlock = getRootBlock(range.startContainer)
    if (startBlock) {
      const key = startBlock.getUniqueId().toString()

      if (!seen.has(key)) {
        seen.add(key)
        blocks.push(startBlock)
      }
    }

    const endBlock = getRootBlock(range.endContainer)

    if (endBlock) {
      const key = endBlock.getUniqueId().toString()

      if (!seen.has(key)) {
        seen.add(key)
        blocks.push(endBlock)
      }
    }

    let node = walker.next()

    while (node) {
      if (node.type === CKEDITOR.NODE_ELEMENT) {
        const el = node as CKEDITOR.dom.element
        const root = getRootBlock(el)

        if (root) {
          const key = root.getUniqueId().toString()

          if (!seen.has(key)) {
            seen.add(key)
            blocks.push(root)
          }
        }
      }
      node = walker.next()
    }
  }

  return blocks
}

const getCurrentFontSize = (
  editor: CKEDITOR.editor,
  defaultSize: number | null
) => {
  const startEl = editor.getSelection()?.getStartElement() ?? null
  const root = getRootBlock(startEl)

  if (root) {
    const size = +root.getAttribute('data-font-size')

    if (size) {
      return size
    }
  }

  return defaultSize
}

const applyFontSize = (
  editor: CKEDITOR.editor,
  size: number,
  defaultSize: number | null
) => {
  editor.focus()
  editor.fire('saveSnapshot')

  let blocks = getSelectedRootBlocks(editor)

  if (blocks.length === 0) {
    const startEl = editor.getSelection()?.getStartElement() ?? null
    const block = getRootBlock(startEl)
    if (!block) {
      editor.fire('saveSnapshot')
      return
    }
    blocks = [block]
  }

  let appliedSize: number | null = null

  for (const block of blocks) {
    if (size === defaultSize) {
      block.removeAttribute('data-font-size')
      appliedSize = defaultSize
    } else if (
      !defaultSize &&
      block.getAttribute('data-font-size') === `${size}`
    ) {
      block.removeAttribute('data-font-size')
    } else {
      block.setAttribute('data-font-size', String(size))
      appliedSize = size
    }
  }

  editor.fire('saveSnapshot')

  return appliedSize
}

const promoteParagraphFontSizeToList = (editor: CKEDITOR.editor) => {
  const body = editor.document.getBody()
  const lists = body.find('ol, ul')

  for (let i = 0; i < lists.count(); i++) {
    const list = lists.getItem(i) as CKEDITOR.dom.element

    if (list.getAttribute('data-font-size')) {
      continue
    }

    const blocks = list.find('[data-font-size]')

    if (blocks.count() === 0) {
      continue
    }

    const firstBlock = blocks.getItem(0) as CKEDITOR.dom.element
    const size = firstBlock.getAttribute('data-font-size')

    if (!size) {
      continue
    }

    list.setAttribute('data-font-size', size)

    const nested = list.find('[data-font-size]')

    for (let j = 0; j < nested.count(); j++) {
      const el = nested.getItem(j) as CKEDITOR.dom.element

      el.removeAttribute('data-font-size')
    }
  }
}

export const fontSizeDropDownPlugin: CKEDITOR.pluginDefinition = {
  requires: 'richcombo',

  init(editor) {
    const NON_SIZABLE_TAGS =
      'fontSizeDropdown_nonSizableTags' in editor.config &&
      Array.isArray(editor.config.fontSizeDropdown_nonSizableTags)
        ? (editor.config.fontSizeDropdown_nonSizableTags as string[])
        : undefined

    const FONT_SIZES =
      'fontSizeDropdown_sizes' in editor.config &&
      Array.isArray(editor.config.fontSizeDropdown_sizes)
        ? (editor.config.fontSizeDropdown_sizes as number[])
        : DEFAUL_FONT_SIZES

    const DEFAULT_SIZE =
      'fontSizeDropdown_defaultSize' in editor.config
        ? (editor.config.fontSizeDropdown_defaultSize as number | null)
        : 24

    editor.addFeature({
      allowedContent: [
        'p[data-font-size]',
        'h1[data-font-size]',
        'h2[data-font-size]',
        'h3[data-font-size]',
        'h4[data-font-size]',
        'h5[data-font-size]',
        'h6[data-font-size]',
        'ol[data-font-size]',
        'ul[data-font-size]',
      ].join(';'),
    })

    editor.on('afterCommandExec', (evt) => {
      const commandName = evt.data.name as string

      if (
        ['numberedlist', 'bulletedlist', 'indent', 'outdent'].includes(
          commandName
        )
      ) {
        editor.fire('saveSnapshot')
        promoteParagraphFontSizeToList(editor)
        editor.fire('saveSnapshot')
      }
    })

    if (NON_SIZABLE_TAGS?.length) {
      const handleChange = debounce(() => {
        const isChanged = cleanupNonSizableTags(editor, NON_SIZABLE_TAGS)

        if (isChanged) {
          editor.fire('updateSnapshot')
          editor.fire('change')
        }
      }, 0)

      editor.on('change', handleChange)

      editor.on('destroy', () => {
        handleChange.cancel()
      })
    }

    let blurFirstElTimeoutId = 0

    editor.ui.addRichCombo('FontSizeDropdown', {
      label: 'Размер',
      title: 'Размер шрифта',
      toolbar: 'insert',

      panel: {
        css: [
          CKEDITOR.skin.getPath('editor'),
          editor.config.contentsCss,
          `${CKEDITOR.basePath}plugins/fontsizedropdown.css`,
        ],
        multiSelect: false,
        attributes: {
          'aria-label': 'Font size dropdown',
        },
      },

      init() {
        FONT_SIZES.forEach((size) => {
          this.add(size.toString(), `${size}\xa0px`)
        })
      },

      onClick(value: string) {
        if (selectionHasNonSizableTag(editor, NON_SIZABLE_TAGS)) {
          return
        }

        const appliedSize = applyFontSize(editor, +value, DEFAULT_SIZE)

        if (appliedSize) {
          this.setValue(appliedSize, `${appliedSize}\xa0px`)
        } else {
          this.setValue('')
        }
      },

      onRender() {
        editor.on('selectionChange', () => {
          if (selectionHasNonSizableTag(editor, NON_SIZABLE_TAGS)) {
            if (DEFAULT_SIZE) {
              this.setValue(DEFAULT_SIZE, `${DEFAULT_SIZE}\xa0px`)
            } else {
              this.setValue('')
            }

            this.disable()

            return
          }

          this.enable()

          const size = getCurrentFontSize(editor, DEFAULT_SIZE)

          if (size) {
            this.setValue(size, `${size}\xa0px`)
          }
        })
      },

      onOpen() {
        window.clearTimeout(blurFirstElTimeoutId)

        if (selectionHasNonSizableTag(editor, NON_SIZABLE_TAGS)) {
          this.disable()
          return
        }

        const size = getCurrentFontSize(editor, DEFAULT_SIZE)

        if (size) {
          this.setValue(size, `${size}\xa0px`)
        } else {
          blurFirstElTimeoutId = window.setTimeout(() => {
            const iframeEl = this._?.panel?._?.iframe?.$ as
              | HTMLIFrameElement
              | undefined

            const activeElement = iframeEl?.contentDocument?.activeElement as
              | HTMLElement
              | undefined

            activeElement?.blur()
          }, 20)
        }
      },
    })

    editor.on('destroy', () => {
      window.clearTimeout(blurFirstElTimeoutId)
    })
  },
}
